import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './SellerDashboard.css';
import {
  Line,
  Pie
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';

import {
  IoCubeOutline,
  IoPulseOutline,
  IoCheckmarkDoneOutline,
  IoCloseCircleOutline,
  IoCalendarOutline,
  IoChevronDownOutline,
  IoCalendarSharp,
} from 'react-icons/io5';

ChartJS.register(LineElement, ArcElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const months = [
  { key: 1, label: 'Jan' }, { key: 2, label: 'Feb' }, { key: 3, label: 'Mar' },
  { key: 4, label: 'Apr' }, { key: 5, label: 'May' }, { key: 6, label: 'Jun' },
  { key: 7, label: 'Jul' }, { key: 8, label: 'Aug' }, { key: 9, label: 'Sep' },
  { key: 10, label: 'Oct' }, { key: 11, label: 'Nov' }, { key: 12, label: 'Dec' },
];

const iconMap = {
  'cube-outline': <IoCubeOutline />,
  'pulse-outline': <IoPulseOutline />,
  'checkmark-done-outline': <IoCheckmarkDoneOutline />,
  'close-circle-outline': <IoCloseCircleOutline />,
};

const SellerDashboard = () => {
  const [sellerId, setSellerId] = useState(null);
  const [stats, setStats] = useState([
    { title: 'Total Products', count: 0, icon: 'cube-outline', bg: '#062d59' },
    { title: 'Live Bids', count: 0, icon: 'pulse-outline', bg: '#274c87' },
    { title: 'Success Bids', count: 0, icon: 'checkmark-done-outline', bg: '#e3080c' },
    { title: 'Expired Bids', count: 0, icon: 'close-circle-outline', bg: '#f14b4e' },
  ]);
  const [matchedBids, setMatchedBids] = useState([]);
  const [acceptedBids, setAcceptedBids] = useState({});
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [revenueData, setRevenueData] = useState([]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const [chartData, setChartData] = useState({
    labels: months.map(m => m.label),
    datasets: [{ data: Array(12).fill(0), borderColor: '#e3080c', tension: 0.3 }]
  });

  useEffect(() => {
  const user = JSON.parse(sessionStorage.getItem('user'));
  const userId = user?.id;
     setSellerId((userId));
  }, []);

  useEffect(() => {
    if (!sellerId) return;

    axios.get(`https://vyaparapi.emedha.in/api/seller/matched-bids/${sellerId}`).then(async res => {
      const matched = res.data.matchedBids || [];
      const statusPromises = matched.map(bid =>
        axios.get(`https://vyaparapi.emedha.in/api/seller/has-accepted?bid_id=${bid.bid_id}&seller_id=${sellerId}`)
          .then(r => ({ bid_id: bid.bid_id, hasAccepted: r.data.hasAccepted }))
      );

      const statuses = await Promise.all(statusPromises);
      const acceptedMap = {};
      statuses.forEach(({ bid_id, hasAccepted }) => {
        acceptedMap[bid_id] = hasAccepted;
      });

      const liveBids = matched.filter(bid => bid.status === 'active');
      const expiredCount = matched.filter(bid => bid.status === 'expired' && !acceptedMap[bid.bid_id]).length;

      setStats(prev =>
        prev.map(stat => {
          if (stat.title === 'Live Bids') return { ...stat, count: liveBids.length };
          if (stat.title === 'Expired Bids') return { ...stat, count: expiredCount };
          return stat;
        })
      );

      setAcceptedBids(acceptedMap);
      setMatchedBids(matched);
    });
  }, [sellerId]);

  useEffect(() => {
    if (!sellerId) return;

    axios.get(`https://vyaparapi.emedha.in/api/seller/${sellerId}/dashboard`).then(res => {
      setStats(prev =>
        prev.map(stat =>
          stat.title === 'Total Products' ? { ...stat, count: res.data.totalProducts } : stat
        )
      );
    });

    axios.get(`https://vyaparapi.emedha.in/api/seller/${sellerId}/success-bids-count`).then(res => {
      setStats(prev =>
        prev.map(stat =>
          stat.title === 'Success Bids' ? { ...stat, count: res.data.successBidsCount } : stat
        )
      );
    });

    axios.get(`https://vyaparapi.emedha.in/api/seller/${sellerId}/monthly-bids?year=${year}`).then(res => {
      setChartData({
        labels: months.map(m => m.label),
        datasets: [{ data: res.data.monthlyCounts, borderColor: '#e3080c', tension: 0.3 }]
      });
    });

    axios.get(`https://vyaparapi.emedha.in/api/seller/${sellerId}/revenue-breakdown?year=${year}&month=${selectedMonth}`)
      .then(res => {
        const pieData = res.data.map(item => ({
          name: item.name,
          value: Number(item.revenue),
          color: item.color || '#ccc'
        }));
        setRevenueData(pieData);
      });
  }, [sellerId, year, selectedMonth]);

  const pieChartData = {
    labels: revenueData.map(d => d.name),
    datasets: [{
      data: revenueData.map(d => d.value),
      backgroundColor: revenueData.map(d => d.color),
    }]
  };

  return (
    <div className="dashboard-container">
      <div className="card-container">
        {stats.map((stat, idx) => (
          <div className="dashboard-card" style={{ backgroundColor: stat.bg }} key={idx}>
            <div className="icon">{iconMap[stat.icon]}</div>
            <div className="card-title">{stat.title}</div>
            <div className="card-count">{stat.count}</div>
          </div>
        ))}
      </div>

<div className="charts-wrapper">
  <div className="chart-section line-chart">
    <div className="section-header">
      <h6>Monthly Bids Overview</h6>
      <select onChange={(e) => setYear(Number(e.target.value))} value={year}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
    <Line data={chartData} />
  </div>

  <div className="chart-section pie-chart">
    <div className="section-header">
      <h6>Revenue Breakdown</h6>
      <div className="filters">
        <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
          {months.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
        <select onChange={(e) => setYear(Number(e.target.value))} value={year}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
    <div style={{ width: '100%', height: '300px' }}>
      <Pie
        data={pieChartData}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
          },
        }}
      />
    </div>
  </div>
</div>


    </div>
  );
};

export default SellerDashboard;
